import { useState } from "react";
import { airlineLogoSrc, showNanodeDevices, themePackLabel } from "@northstar/dispatch-core";
import { NanodeDevicesCard } from "./NanodeDevicesCard";
import { companionRuntimeApiBase, HOSTED_API_BASE } from "../apiBase";
import { COMPANION_PRODUCT, COMPANION_SCOPE, NANODE_SYNC_HINT_URL } from "../companion";
import type { CompanionConstantMs } from "../refreshSettings";
import type { CompanionApi } from "../useCompanion";
import { APP_VERSION } from "../version";
import { ReleaseNotes } from "./ReleaseNotes";

export function MoreView({ api }: { api: CompanionApi }) {
  const { settings, account, view, chrome, hosted } = api;
  const shownBase = hosted ? settings.baseUrl || companionRuntimeApiBase() || HOSTED_API_BASE : settings.baseUrl;

  return (
    <div className="more-stack" data-testid="more-view">
      <p className="kicker">More</p>
      <h1>Connect</h1>
      <p className="muted">{COMPANION_SCOPE}</p>
      <p className="muted">Megan / dispatch voice stays on the desk phone (PSTN). This app does not place or take those calls.</p>

      {hosted ? (
        <section className="panel bevel-out" data-testid="phone-while-out">
          <p className="kicker">Phone while out</p>
          <p className="muted">
            {shownBase.startsWith("http") ? (
              <>
                This app talks to the Nanode through <span className="mono">{shownBase}</span>. The company{" "}
                <span className="mono">SYNC_TOKEN</span> is not stored on the phone.
              </>
            ) : (
              <>
                This HTTPS site talks to the Nanode through a same-origin <span className="mono">{shownBase}</span>{" "}
                proxy. The company <span className="mono">SYNC_TOKEN</span> is not stored on the phone.
              </>
            )}
          </p>
          <p className="field-hint" data-testid="install-steps">
            When the browser can install, a banner offers Install. iPhone Safari has no install prompt: Share → Add
            to Home Screen. Chrome on Android uses that banner or the browser menu.
          </p>
        </section>
      ) : null}

      <form className="panel bevel-out" onSubmit={(e) => e.preventDefault()}>
        <p className="kicker">Nanode sync</p>
        <div className="sync-status" data-status={api.status}>
          <span
            className={`led led-${
              api.status === "ok" ? "live" : api.status === "error" ? "offline" : api.status === "checking" ? "idle" : "disabled"
            }`}
            aria-hidden
          />
          <span>
            <strong>
              {api.status === "ok"
                ? "Sync server reachable"
                : api.status === "error"
                  ? "Sync server unreachable"
                  : api.status === "checking"
                    ? "Checking…"
                    : "Local cache only"}
            </strong>
            {api.healthError ? <span className="field-hint"> — {api.healthError}</span> : null}
          </span>
        </div>
        <div className="field">
          <label htmlFor="sync-url">Sync server base URL</label>
          <input
            id="sync-url"
            value={shownBase}
            autoComplete="off"
            readOnly={hosted}
            placeholder={hosted ? shownBase : NANODE_SYNC_HINT_URL}
            onChange={(e) => {
              if (!hosted) api.setSettings({ baseUrl: e.target.value });
            }}
          />
          <span className="field-hint">
            {hosted
              ? "This hosted app uses the HTTPS proxy. Plain HTTP Nanode is not called from this page (mixed content)."
              : `Same host the desk uses. Hint is the ops Nanode — change it if you run a local stub (${"http://127.0.0.1:8787"}).`}
          </span>
        </div>
        {hosted ? null : (
          <div className="field">
            <label htmlFor="sync-token">API token</label>
            <input
              id="sync-token"
              type="password"
              autoComplete="off"
              placeholder="Matches SYNC_TOKEN — stored on this phone only"
              value={settings.token}
              onChange={(e) => api.setSettings({ token: e.target.value })}
            />
          </div>
        )}
        <div className="row">
          <button type="button" className="btn ghost" disabled={!api.canProbe} onClick={() => void api.probe()}>
            Check server
          </button>
          <button type="button" className="btn" disabled={!api.configured || api.busy} onClick={() => void api.pull()}>
            Pull snapshot
          </button>
        </div>
      </form>

      <form className="panel bevel-out" onSubmit={(e) => e.preventDefault()}>
        <p className="kicker">Nanode account</p>
        {account ? (
          <p className="ok">
            Signed in as <span className="mono">{account.email}</span>
          </p>
        ) : (
          <p className="muted">
            {hosted
              ? "Same email + password as the desk. Sign in, then Pull snapshot."
              : "Same email + password as the desk. This app never saves a career blob."}
          </p>
        )}
        <AccountFields api={api} />
      </form>

      {showNanodeDevices() ? <NanodeDevicesCard devices={[]} timeZone="UTC" browserEnroll /> : null}

      <form className="panel bevel-out" onSubmit={(e) => e.preventDefault()}>
        <p className="kicker">Refresh</p>
        <p className="muted">
          Two channels: live track (~1s kinematics only) and the board (full snapshot only when
          the desk publishes a schedule / assignment / company change).
        </p>
        <div className="field">
          <label htmlFor="refresh-mode">When to listen</label>
          <select
            id="refresh-mode"
            value={api.refresh.mode}
            onChange={(e) => api.setRefresh({ mode: e.target.value === "constant" ? "constant" : "on-screen" })}
          >
            <option value="on-screen">Only when on screen</option>
            <option value="constant">Constant updating</option>
          </select>
          <span className="field-hint">
            Cheap revision ping + live slice while this mode is active. Idle board = no full
            snapshot traffic. Phones may throttle Constant in the background.
          </span>
        </div>
        {api.refresh.mode === "constant" ? (
          <div className="field">
            <label htmlFor="refresh-ms">Fallback snapshot</label>
            <select
              id="refresh-ms"
              value={String(api.refresh.constantMs)}
              onChange={(e) => api.setRefresh({ constantMs: Number(e.target.value) as CompanionConstantMs })}
            >
              <option value="15000">15 seconds</option>
              <option value="30000">30 seconds</option>
            </select>
            <span className="field-hint">Only if the revision ping is down. On-screen fallback is 5s.</span>
          </div>
        ) : null}
      </form>

      <section className="panel bevel-out" data-testid="company-membership">
        <p className="kicker">Company membership</p>
        <h2 className="company-heading">
          {airlineLogoSrc(view.companyLogo) ? (
            <img className="company-kicker-logo" src={airlineLogoSrc(view.companyLogo) ?? ""} alt="" />
          ) : null}
          {view.companyName || "No joined company"}
        </h2>
        <p className="muted">
          {view.companyName
            ? `Shared board for ${view.companyName}${view.homeIcao ? ` · ${view.homeIcao}` : ""}. ${
                view.canDispatchWrite
                  ? "Dispatcher sees the company board and can cancel / edit STD through Nanode."
                  : "This phone shows only your crew trips in that company."
              }`
            : "Join or publish a company on the Windows desk, then Pull. Private careers do not mix on this phone."}
        </p>
        <p className="field-hint">Leave or switch company on the desk. This phone updates on the next board nudge.</p>
        {view.boardScope === "unmatched-company" ? (
          <p className="field-hint" data-testid="board-fallback">
            This sign-in did not match a crew name. Schedule is the company board — the same CEL#### / F=ferry
            tickets as the desk — until you pick your name below.
          </p>
        ) : null}
        {api.boardStatus ? (
          <p className="field-hint" data-testid="board-status">
            {api.boardStatus}
          </p>
        ) : null}
        {view.schedulePolicy.scheduleOnlyContentXmlAirports ? (
          <p className="field-hint" data-testid="content-xml-schedule">
            Schedule only Content.xml airports is on. Dest / origin writes must stay in the scanned list
            {view.schedulePolicy.contentXmlIcaos.length
              ? ` (${view.schedulePolicy.contentXmlIcaos.slice(0, 8).join(", ")}${
                  view.schedulePolicy.contentXmlIcaos.length > 8 ? "…" : ""
                })`
              : " — list is empty, so a dest/origin change is refused until the desk rescans"}.
          </p>
        ) : null}
      </section>

      <form className="panel bevel-out" onSubmit={(e) => e.preventDefault()}>
        <p className="kicker">Crew focus</p>
        <p className="muted">
          {view.canDispatchWrite
            ? "Dispatcher focus unlocks company-board cancel / STD edit. Pick your desk name if it did not auto-match."
            : "This phone shows only the signed-in crew’s trips. Pick yourself if the name did not auto-match."}
        </p>
        <div className="field">
          <label htmlFor="crew-id">Crew on this phone</label>
          <select id="crew-id" value={api.crewId} onChange={(e) => api.setCrewId(e.target.value)}>
            <option value="">Pick your name</option>
            {view.roster.map((row) => (
              <option key={row.id} value={row.id}>
                {row.displayName}
              </option>
            ))}
          </select>
        </div>
      </form>

      <section className="panel bevel-out" data-testid="theme-follows-desk">
        <p className="kicker">Theme</p>
        <h2>{themePackLabel(chrome.themePack)}</h2>
        <p className="muted">
          Matches the desk for this joined company ({chrome.theme === "light" ? "Day" : "Night"} · {chrome.themeAccent}).
          Desk is the source of truth — night or day follows that snapshot, not this phone’s system appearance.
          This phone has no theme picker.
        </p>
      </section>

      <section className="panel bevel-out">
        <p className="kicker">About</p>
        <h2>{COMPANION_PRODUCT}</h2>
        <p className="mono">Version {APP_VERSION}</p>
        <p className="muted">
          Northstar is the software vendor. The company on the snapshot is the operator
          {view.companyName ? ` (${view.companyName})` : ""}.
        </p>
        {view.pulledAtUtc ? <p className="field-hint">Last snapshot {view.pulledAtUtc}</p> : null}
        <p className="field-hint">What’s new: Release Notes below. Same number as the header.</p>
        <p className="muted">Phone errors stay on this device. The desk Diagnostics page keeps the shared error log.</p>
      </section>

      <ReleaseNotes />

      {api.note ? <p className="ok">{api.note}</p> : null}
      {api.error ? <p className="err">{api.error}</p> : null}
    </div>
  );
}

function AccountFields({ api }: { api: CompanionApi }) {
  const [email, setEmail] = useState(api.account?.email ?? "");
  const [password, setPassword] = useState("");
  const [baseUrl, setBaseUrl] = useState(api.account?.baseUrl || api.settings.baseUrl || "");
  const shownBase = api.hosted ? api.settings.baseUrl || companionRuntimeApiBase() || HOSTED_API_BASE : baseUrl;

  return (
    <>
      {api.hosted ? null : (
        <div className="field">
          <label htmlFor="acct-url">Account sync server</label>
          <input
            id="acct-url"
            value={baseUrl}
            placeholder={NANODE_SYNC_HINT_URL}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        </div>
      )}
      <div className="field">
        <label htmlFor="acct-email">Email</label>
        <input
          id="acct-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="acct-pass">Password</label>
        <input
          id="acct-pass"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={api.busy}
          onClick={() => void api.signIn({ email, password, baseUrl: api.hosted ? shownBase : baseUrl })}
        >
          Sign in
        </button>
        {api.account ? (
          <button type="button" className="btn ghost" onClick={() => void api.signOut()}>
            Sign out / switch account
          </button>
        ) : null}
      </div>
    </>
  );
}
